import { useMemo } from 'react';

import { type KeyCatalogueEntry, type KeyValueKind } from '../types/key-value.types';

export interface UseKeyValueCatalogueInput {
  catalogue: readonly KeyCatalogueEntry[] | undefined;
  /** What a key the catalogue does not describe is edited as. */
  defaultValueKind: KeyValueKind;
  isKeyRestricted?: boolean;
}

export interface UseKeyValueCatalogue {
  hasCatalogue: boolean;
  /**
   * The catalogue entries not on screen yet, in catalogue order.
   *
   * A function rather than a value because the rows are what "on screen" means,
   * and the rows need this hook's lookups to exist first. Taking the present
   * keys as an input would make the two hooks mutually dependent — and reading
   * them off the emitted `value` instead is subtly wrong: a freshly added
   * numeric row has a key and no value yet, so it is not in the map, and the add
   * menu would go on offering a key already sitting on screen.
   */
  remainingFor: (presentKeys: readonly string[]) => KeyCatalogueEntry[];
  entryFor: (key: string) => KeyCatalogueEntry | null;
  kindFor: (key: string) => KeyValueKind;
  isRequired: (key: string) => boolean;
  isKeyAllowed: (key: string) => boolean;
  /** The display order: catalogue keys first in catalogue order, then the rest. */
  orderKeys: (keys: readonly string[]) => string[];
}

/**
 * What the caller knows about the keys in advance.
 *
 * The order this produces is **derived, not stored**, and that is a decision
 * rather than a shortcut: `setting_values.value` is a `jsonb` column, and
 * PostgreSQL does not preserve object key order — so a row an operator dragged
 * would survive until Save and then visibly jump back on the refetch. A
 * catalogue order is deterministic, identical for every operator and every
 * reload, and better than anything a drag could produce. (Persisting a chosen
 * order needs an array-shaped setting type; recorded in PROGRESS.)
 */
export function useKeyValueCatalogue({
  catalogue,
  defaultValueKind,
  isKeyRestricted = false,
}: UseKeyValueCatalogueInput): UseKeyValueCatalogue {
  const byKey = useMemo(() => {
    const map = new Map<string, KeyCatalogueEntry>();
    for (const entry of catalogue ?? []) map.set(entry.key, entry);
    return map;
  }, [catalogue]);

  /**
   * One memo over the catalogue itself, so every lookup keeps its identity for
   * as long as the catalogue does.
   *
   * That matters downstream: `useKeyValueRows` builds its row view models in a
   * memo keyed on `kindFor`/`entryFor`/`isRequired`, and a fresh set of those per
   * render would rebuild every row on every keystroke for lookups that did not
   * change.
   */
  return useMemo(() => {
    const entries = catalogue ?? [];
    const order = new Map(entries.map((entry, index) => [entry.key, index]));
    const entryFor = (key: string): KeyCatalogueEntry | null => byKey.get(key) ?? null;

    return {
      hasCatalogue: entries.length > 0,
      remainingFor: (presentKeys: readonly string[]): KeyCatalogueEntry[] => {
        const present = new Set(presentKeys);
        return entries.filter((entry) => !present.has(entry.key));
      },
      entryFor,
      kindFor: (key: string): KeyValueKind => entryFor(key)?.valueKind ?? defaultValueKind,
      isRequired: (key: string): boolean => entryFor(key)?.isRequired === true,
      isKeyAllowed: (key: string): boolean => !isKeyRestricted || byKey.has(key),
      orderKeys: (keys: readonly string[]): string[] =>
        [...keys].sort((left, right) => {
          const leftAt = order.get(left);
          const rightAt = order.get(right);

          if (leftAt === undefined && rightAt === undefined) return 0;
          // A key the catalogue does not know sorts after every one it does,
          // and keeps its incoming order among its own kind (Array#sort is
          // stable, so returning 0 above is what preserves it).
          if (leftAt === undefined) return 1;
          if (rightAt === undefined) return -1;
          return leftAt - rightAt;
        }),
    };
  }, [byKey, catalogue, defaultValueKind, isKeyRestricted]);
}
